/* eslint-disable indent */
import React, { forwardRef, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ACTIONS, EVENTS, STATUS } from "react-joyride";
import { debounce } from "lodash";
import { Download, Edit, Expand, Eye, EyeOff, Heart, ImageMinus, MoreHorizontal, Palette, RefreshCw, Share2, SmilePlus, Trash2, Trophy } from "lucide-react";
import CustomJoyride from "@/components/atoms/Joyride";
import { useTutorial } from "@/components/organisms/TutorialProvider";
import { Popover, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ImageDTO } from "@/types";
import { controlsContainerTutorialSteps } from "@/utils/joyride/controlsContainerTutorialSteps";
import * as Styled from "./ControlsContainer.styled";
interface ControlsContainerProps {
  isFirstImage?: boolean;
  imageInfo: ImageDTO;
  handleToggle: (id: string) => void;
  handleShareClick: (e: React.MouseEvent, imageId?: string) => void;
  handleCompetitionsClick: (imageId?: string) => void;
  handleEditClick: (imageId?: string) => void;
  handleADetailerClick: (imageId?: string) => void;
  handleFaceSwapClick: (imageId?: string) => void;
  handleBGRemovalClick: (imageId?: string) => void;
  handleInpaintClick: (imageId?: string) => void;
  handleUpscaleClick: (imageId?: string) => void;
  prepareForDelete: (id: string) => void;
  handleVote: (id: string) => void;
  isUserImage: (username: string) => boolean;
  showLabel?: boolean;
}
const ControlsContainer: React.FC<ControlsContainerProps> = ({
  isFirstImage,
  imageInfo,
  handleToggle,
  handleShareClick,
  handleCompetitionsClick,
  handleADetailerClick,
  handleFaceSwapClick,
  handleBGRemovalClick,
  handleInpaintClick,
  handleUpscaleClick,
  prepareForDelete,
  handleVote,
  isUserImage,
  showLabel = false
}) => {
  const controlsRef = useRef<HTMLDivElement>(null);
  const [visibleControls, setVisibleControls] = useState<any[]>([]);
  const [overflowControls, setOverflowControls] = useState<any[]>([]);
  const isOwnedByUser = isUserImage(imageInfo.imageMetadata.username);
  const {
    tutorialState,
    tutorialStateInitialized,
    startTutorial,
    completeTutorial
  } = useTutorial();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const popoverButtonRef = useRef<HTMLButtonElement>(null);
  interface ControlButtonProps {
    icon: React.ComponentType<{
      className?: string;
    }>;
    onClick: (e: React.MouseEvent) => void;
    tooltip: string;
    className?: string;
    label?: string;
    showLabel?: boolean;
  }
  useEffect(() => {
    if (!tutorialStateInitialized || !tutorialState || showLabel) return;
    if (tutorialState.imageViewer.completed && !tutorialState.controlsContainer.completed && !tutorialState.controlsContainer.started && isFirstImage) {
      startTutorial("controlsContainer");
    }
  }, [tutorialState, startTutorial, isFirstImage, tutorialStateInitialized, showLabel]);
  useEffect(() => {
    if (tutorialState?.controlsContainer.started && !tutorialState.controlsContainer.completed && isFirstImage && !showLabel) {
      setRun(true);
    }
  }, [isFirstImage, showLabel, tutorialState]);
  const handleControlsTutorialCallback = (data: {
    status: any;
    type: any;
    action: any;
    index: any;
  }) => {
    const {
      status,
      type,
      action,
      index
    } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status) || action === ACTIONS.CLOSE) {
      // Tutorial has finished or been skipped
      completeTutorial("controlsContainer");
      setRun(false);
      setStepIndex(0);
      const event = new MouseEvent("click", {
        bubbles: true
      });
      handleShareClick(event as unknown as React.MouseEvent<Element, MouseEvent>, imageInfo.id);
    } else if (type === EVENTS.STEP_BEFORE) {
      // Handle actions before each step
      const currentStep = controlsContainerTutorialSteps[index];
      if (currentStep.target === ".joyride-ellipsis-button" && action === ACTIONS.NEXT) {
        if (popoverButtonRef.current) {
          popoverButtonRef.current.click();
        }
      }
      if (currentStep.target === ".joyride-heart-icon" && action === ACTIONS.NEXT) {
        if (popoverButtonRef.current) {
          popoverButtonRef.current.click();
        }
      }
      // Add other actions for steps within ControlsContainer
    } else if (type === EVENTS.STEP_AFTER) {
      // Update the step index after each step
      if (action === ACTIONS.NEXT) {
        setStepIndex(prev => prev + 1);
      } else if (action === ACTIONS.PREV) {
        setStepIndex(prev => prev - 1);
      }
    }
  };

  // Modify the ControlButton component to forward refs
  const ControlButton = forwardRef<HTMLDivElement, ControlButtonProps>(({
    icon: Icon,
    onClick,
    tooltip,
    className = "",
    label,
    showLabel = false
  }, ref) => <div ref={ref}>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Styled.PopoverItem variant="ghost" size="icon" className={`h-8 w-8 ${className}`} onClick={onClick}>
                            <Icon className="h-4 w-4" />
                            {label && showLabel && <span className="ml-2">{label}</span>}
                        </Styled.PopoverItem>
                    </TooltipTrigger>
                    <TooltipContent>{tooltip}</TooltipContent>
                </Tooltip>
            </div>);
  // Add displayName to the component
  ControlButton.displayName = "ControlButton";

  // Initialize refs for fixed controls
  const fixedControlRefs = useRef<{
    [key: string]: HTMLDivElement | null;
  }>({});

  // Initialize refs for expandable controls
  const expandableControlRefs = useRef<{
    [key: string]: HTMLDivElement | null;
  }>({});

  // Define fixed controls data
  const fixedControls = [{
    key: "like",
    render: (ref: React.Ref<HTMLDivElement>) => <div ref={ref}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Styled.ActionButton $isLiked={imageInfo.isVotedByUser} onClick={e => {
            e.stopPropagation();
            handleVote(imageInfo.id);
          }}>
                                <Heart className={`h-4 w-4 mr-2 ${imageInfo.isVotedByUser ? "fill-rose-500" : ""}`} />
                                <span>{imageInfo.voteCount}</span>
                            </Styled.ActionButton>
                        </TooltipTrigger>
                        <TooltipContent>
                            {imageInfo.isVotedByUser ? "Unlike this image" : "Like this image"}
                        </TooltipContent>
                    </Tooltip>
                </div>
  }, {
    key: "share",
    render: (ref: React.Ref<HTMLDivElement>) => <ControlButton ref={ref} icon={Share2} onClick={e => handleShareClick(e, imageInfo.id)} tooltip="Share this image and earn Gold for each share." className={isFirstImage ? "joyride-share-button text-white hover:text-primary" : "text-white hover:text-primary"} />
  }, ...(isOwnedByUser ? [{
    key: "visibility",
    render: (ref: React.Ref<HTMLDivElement>) => <div ref={ref}>
                              <Styled.TogglePublicButton $isPublic={imageInfo.isPublic} onClick={e => {
        e.stopPropagation();
        handleToggle(imageInfo.id);
      }} className={isFirstImage ? "joyride-public-toggle h-8 w-8 " : "h-8 w-8 "}>
                                  {imageInfo.isPublic ? <Eye className="h-4 w-4 mr-2" /> : <EyeOff className="h-4 w-4 mr-2" />}
                              </Styled.TogglePublicButton>
                          </div>
  }] : [])];

  // Define expandable controls data
  const expandableControls = React.useMemo(() => [{
    key: "download",
    label: "Download",
    icon: Download,
    onClick: () => {
      const link = document.createElement("a");
      link.href = imageInfo.image;
      link.download = "image.jpg";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    tooltip: "Download",
    className: isFirstImage ? "joyride-download-option" : ""
  }, ...(isOwnedByUser ? [{
    key: "competitions",
    label: "Enter Competition",
    icon: Trophy,
    onClick: () => handleCompetitionsClick(imageInfo.id),
    tooltip: "Enter a Competition and win gold!",
    className: isFirstImage ? "joyride-competition-option" : ""
  }, {
    key: "inpaint",
    label: "Selective Edit",
    icon: Edit,
    onClick: () => handleInpaintClick(imageInfo.id),
    tooltip: "Edit part of an image keeping the rest the same.",
    className: ""
  }, {
    key: "faceswap",
    label: "Face Swap",
    icon: RefreshCw,
    onClick: () => handleFaceSwapClick(imageInfo.id),
    tooltip: "Swap a Face in the Image with an uploaded Face",
    className: isFirstImage ? "joyride-swap-faces-option" : ""
  }, {
    key: "delete",
    label: "Delete",
    icon: Trash2,
    onClick: () => prepareForDelete(imageInfo.id),
    tooltip: "Delete",
    className: isFirstImage ? "joyride-delete-option" : ""
  }, {
    key: "upscale",
    label: "Upscale",
    icon: Expand,
    onClick: () => handleUpscaleClick(imageInfo.id),
    tooltip: "Upscale an image resolution upto 4x",
    className: isFirstImage ? "joyride-upscale-option" : ""
  }, {
    key: "adetailer",
    label: "Fix Faces",
    icon: SmilePlus,
    onClick: () => handleADetailerClick(imageInfo.id),
    tooltip: "Refine Faces only, keeping the rest of the image the same",
    className: isFirstImage ? "joyride-refine-faces-option" : ""
  }, {
    key: "bgremoval",
    label: "Remove Background",
    icon: ImageMinus,
    onClick: () => handleBGRemovalClick(imageInfo.id),
    tooltip: "Remove the Background from the Image",
    className: isFirstImage ? "joyride-remove-bg-option" : ""
  }, {
    key: "edit",
    label: "Edit",
    icon: Palette,
    onClick: () => handleInpaintClick(imageInfo.id),
    tooltip: "Edit part of an image keeping the rest the same.",
    className: isFirstImage ? "joyride-spot-edit-option" : ""
  }] : [])], [isOwnedByUser, imageInfo.image, imageInfo.id, handleCompetitionsClick, handleInpaintClick, prepareForDelete, handleUpscaleClick, handleADetailerClick, handleFaceSwapClick, handleBGRemovalClick, isFirstImage]);

  // Hidden container for measuring controls
  const hiddenContainerRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const updateControls = () => {
      if (controlsRef.current) {
        const containerWidth = controlsRef.current.offsetWidth;
        let currentWidth = 0;
        const visible: React.SetStateAction<any[]> = [];
        const overflow: React.SetStateAction<any[]> = [];
        expandableControls.forEach(control => {
          const el = expandableControlRefs.current[control.key];
          const width = el?.offsetWidth || 0;
          if (currentWidth + width <= containerWidth) {
            visible.push(control);
            currentWidth += width;
          } else {
            overflow.push(control);
          }
        });
        setVisibleControls(visible);
        setOverflowControls(overflow);
      }
    };
    const debouncedUpdateControls = debounce(updateControls, 100);
    updateControls();
    window.addEventListener("resize", debouncedUpdateControls);
    return () => window.removeEventListener("resize", debouncedUpdateControls);
  }, [imageInfo, isOwnedByUser, fixedControls.length, expandableControls.length, showLabel, expandableControls]);
  return <Styled.Container className={isFirstImage ? "joyride-controls-container" : ""} ref={controlsRef} data-sentry-element="unknown" data-sentry-component="ControlsContainer" data-sentry-source-file="index.tsx">
            <TooltipProvider data-sentry-element="TooltipProvider" data-sentry-source-file="index.tsx">
                <CustomJoyride steps={controlsContainerTutorialSteps} run={run} stepIndex={stepIndex} disableScrolling callback={handleControlsTutorialCallback} data-sentry-element="CustomJoyride" data-sentry-source-file="index.tsx" />
                {/* Hidden container for measuring controls */}
                <div ref={hiddenContainerRef} style={{
        position: "absolute",
        visibility: "hidden",
        height: 0,
        overflow: "hidden"
      }}>
                    {/* Render fixed controls for measurement */}
                    {fixedControls.map(control => <div key={control.key} ref={el => {
          fixedControlRefs.current[control.key] = el;
        }}>
                            {control.render(null)}
                        </div>)}

                    {/* Render expandable controls for measurement */}
                    {expandableControls.map(control => <ControlButton key={control.key} ref={el => {
          expandableControlRefs.current[control.key] = el;
        }} icon={control.icon} onClick={control.onClick} tooltip={control.tooltip} label={control.label} showLabel={true} />)}
                </div>

                <Styled.ControlsContainer className={isFirstImage ? "joyride-controls-bar" : ""} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    {/* Left Side - Fixed Controls */}
                    <div className="flex items-center space-x-2">
                        <div key={fixedControls[0].key}>
                            {fixedControls[0].render(null)}
                        </div>
                    </div>

                    {/* Center - Expanded Controls */}
                    <div className="flex flex-1 items-center">
                        {visibleControls.map(control => {
            if (control.key === "download") {
              return <a href={imageInfo.image} download target="_blank" key={control.key}>
                                        <ControlButton icon={control.icon} onClick={control.onClick} tooltip={control.tooltip} label={control.label} showLabel={showLabel} />
                                    </a>;
            }
            return <ControlButton key={control.key} icon={control.icon} onClick={control.onClick} tooltip={control.tooltip} label={control.label} showLabel={showLabel} />;
          })}
                    </div>

                    <div className="flex items-center space-x-2">
                        {fixedControls.slice(1).map(control => <div key={control.key}>{control.render(null)}</div>)}
                    </div>

                    {/* Right Side - Overflow Menu */}
                    <Styled.ButtonContainer data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        {overflowControls.length > 0 && <Popover>
                                <PopoverTrigger asChild>
                                    <Styled.IconButton className={isFirstImage ? "joyride-ellipsis-button" : ""}>
                                        <MoreHorizontal className="h-4 w-4" />
                                    </Styled.IconButton>
                                </PopoverTrigger>
                                <Styled.PopoverContent className="w-48">
                                    <div className="grid gap-2">
                                        {overflowControls.map(control => {
                  if (control.key === "download") {
                    return <a href={imageInfo.image} download target="_blank" key={control.key}>
                                                        <ControlButton icon={control.icon} onClick={control.onClick} tooltip={control.tooltip} label={control.label} showLabel={true} className={control.className} />
                                                    </a>;
                  }
                  return <ControlButton key={control.key} icon={control.icon} onClick={control.onClick} tooltip={control.tooltip} label={control.label} showLabel={true} className={control.className} />;
                })}
                                    </div>
                                </Styled.PopoverContent>
                            </Popover>}
                    </Styled.ButtonContainer>
                </Styled.ControlsContainer>

                {/* Visibility Message */}
                {isOwnedByUser && <Styled.VisibilityMessage>
                        {imageInfo.isPublic ? "Visible to all" : "Only visible to you"}{" "}
                        - Tap eye icon to change
                    </Styled.VisibilityMessage>}
            </TooltipProvider>
        </Styled.Container>;
};
export default ControlsContainer;